import type { FC } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import { styled } from '@compiled/react';

import Link from '@atlaskit/link';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled.ts';
import { TEMPLATE_GALLERY } from '@confluence/named-routes/entry-points/routes.ts';
import { StaticNudge } from '@confluence/onboarding-hover-nudge/entry-points/StaticNudge.ts';
import { useTemplatesCollectionEligible } from '@confluence/onboarding-helpers/entry-points/hooks/useTemplatesCollectionEligible.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import {
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ONBOARDING_TEMPLATE_COLLECTIONS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

import { TemplatesList } from './TemplatesList.tsx';
import { getTeamBasedTemplates } from './getTeamBasedTemplates.tsx';
import { useCreateFromTemplateAnalyticsEvents } from './useCreateFromTemplateAnalyticsEvents.ts';

const i18n = defineMessages({
	header: {
		id: 'home.views.create-from-templates.header',
		defaultMessage: 'Create from a template',
		description: 'Header for the Create From Templates section',
	},
	viewAll: {
		id: 'home.views.create-from-templates.view-all',
		defaultMessage: 'View all',
		description: 'Link to view all the options available',
	},
	defaultGuideArrowText: {
		id: 'home.views.create-from-templates.guide-arrow-text',
		defaultMessage: 'These templates are popular with people getting started on Confluence',
		description: 'Guide text explaining what templates section does',
	},
	guideArrowTextForSpecificTeam: {
		id: 'home.views.create-from-templates.guide-arrow-text-for-specific-team',
		defaultMessage: 'These templates are popular with people who do work similar to yours.',
		description: 'Guide text explaining what templates section does',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginBottom: token('space.200'),
});

// Setting up an arrow placeholder with height 34 (arrow icon height)
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArrowPlaceholder = styled.div({
	height: '34px',
});

export const TemplateCollections: FC = withTransparentErrorBoundary({
	attribution: Attribution.FLYWHEEL_ACTIVATION,
})(() => {
	const { shouldShowGuidingArrow, teamType, personalSpaceKey, teamTypeLoading, teamTypeError } =
		useTemplatesCollectionEligible();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isWhiteBoardEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const guideArrowText = teamType ? i18n.guideArrowTextForSpecificTeam : i18n.defaultGuideArrowText;

	const templateKeys = getTeamBasedTemplates(teamType ?? undefined, isWhiteBoardEnabled);

	const [hasTemplatesRendered, setHasTemplatesRendered] = useState(false);

	const onRenderingDone = () => setHasTemplatesRendered(true);

	const sendCreateFromTemplateAnalyticsEvent = useCreateFromTemplateAnalyticsEvents();

	const handleClick = () => {
		sendCreateFromTemplateAnalyticsEvent({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'viewAllTemplatesLink',
			attributes: {
				teamType,
			},
		});
	};

	useEffect(() => {
		if (!teamTypeLoading) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'templatesCollection',
					source: 'homeOnboarding',
					attributes: {
						teamType,
					},
				},
			}).fire();

			experienceTracker.succeed({
				name: ONBOARDING_TEMPLATE_COLLECTIONS_EXPERIENCE,
			});
		}
	}, [createAnalyticsEvent, teamType, teamTypeLoading, experienceTracker]);

	useEffect(() => {
		if (teamTypeError) {
			experienceTracker.fail({
				name: ONBOARDING_TEMPLATE_COLLECTIONS_EXPERIENCE,
				error: teamTypeError,
			});
		}
	}, [experienceTracker, teamTypeError]);

	return (
		<>
			<ExperienceStart name={ONBOARDING_TEMPLATE_COLLECTIONS_EXPERIENCE} />
			<Header>
				<Heading size="xsmall" as="h2">
					<FormattedMessage {...i18n.header} />
				</Heading>
				<Link href={String(TEMPLATE_GALLERY.toUrl())} onClick={handleClick}>
					<FormattedMessage {...i18n.viewAll} />
				</Link>
			</Header>
			<TemplatesList
				teamType={teamType ?? ''}
				teamTypeLoading={teamTypeLoading}
				templateKeys={templateKeys}
				personalSpaceKey={personalSpaceKey ?? ''}
				onRenderingDone={onRenderingDone}
			/>
			{shouldShowGuidingArrow && (
				<>
					{hasTemplatesRendered ? (
						<StaticNudge
							customStyles={{
								color: token('color.text.accent.purple'),
								marginTop: token('space.100'),
							}}
							position="up"
							text={<FormattedMessage {...guideArrowText} />}
						/>
					) : (
						<ArrowPlaceholder />
					)}
				</>
			)}
		</>
	);
});
